import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Loader } from 'lucide-react';

interface SpeechInputProps {
  onTranscript: (text: string) => void;
  placeholder?: string;
  className?: string;
  defaultValue?: string;
}

export function SpeechInput({ 
  onTranscript, 
  placeholder = 'Click the microphone to start speaking...', 
  className = '',
  defaultValue = ''
}: SpeechInputProps) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState(defaultValue);
  const [interimTranscript, setInterimTranscript] = useState('');
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (!isInitialMount.current) {
      setTranscript(defaultValue);
    }
    isInitialMount.current = false;
  }, [defaultValue]);

  useEffect(() => {
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            finalTranscript += result[0].transcript + ' ';
          } else {
            interimTranscript += result[0].transcript;
          }
        }

        if (finalTranscript) {
          setTranscript((prev) => {
            const newText = (prev + ' ' + finalTranscript).trim();
            onTranscript(newText);
            return newText;
          });
        }
        setInterimTranscript(interimTranscript);
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'no-speech') {
          // Restart recognition if no speech was detected
          recognition.stop();
          setTimeout(() => {
            if (isListening) {
              recognition.start();
            }
          }, 100);
        } else {
          setIsListening(false);
        }
      };

      recognition.onend = () => {
        // Only stop if we intended to stop
        if (isListening) {
          recognition.start();
        } else {
          setIsListening(false);
          setInterimTranscript('');
        }
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [onTranscript]);

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setTranscript(newText);
    onTranscript(newText);
  };

  return (
    <div className={`relative ${className}`}>
      <textarea
        value={transcript + (interimTranscript ? ' ' + interimTranscript : '')}
        onChange={handleTextareaChange}
        placeholder={placeholder}
        className="w-full min-h-[150px] p-4 pr-12 rounded-lg border border-gray-300 focus:border-primary-400 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
      />
      <button
        onClick={toggleListening}
        type="button"
        className={`absolute right-3 top-3 p-2 rounded-full transition-colors ${
          isListening 
            ? 'bg-red-100 text-red-600 hover:bg-red-200' 
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
        title={isListening ? 'Stop listening' : 'Start listening'}
      >
        {isListening ? (
          <div className="relative">
            <MicOff className="w-5 h-5" />
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          </div>
        ) : (
          <Mic className="w-5 h-5" />
        )}
      </button>
    </div>
  );
}